import { Box, Checkbox, Dialog, DialogActions, DialogTitle, FormControlLabel, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../common/ui/GeneralButton";
import { useState } from "react";
import { GenericInAppNotification } from "../../types/notificationType";
import TranslatableTextField from "../../components/common/translations/TranslatableTextField";
import SelectGroupsList from "../../components/groups/SelectGroupsList";
import SelectEmployeesList from "../../components/employees/SelectEmployeesList";

interface Props {
    onSave: (notification: GenericInAppNotification) => Promise<void>;
    onDelete?: (notification: GenericInAppNotification) => Promise<void>;
    onClose: () => void;

    editingNotification?: GenericInAppNotification;
}

export default function CreateOrEditGenericNotificationDialog(props: Props) {
    const { t } = useTranslation();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [formData, setFormData] = useState<GenericInAppNotification>(props.editingNotification ?? {
        message: "",
        title: "",
        isCompanyWideAssigned: false,
        assignedEmployeeUserIds: [],
        assignedGroupIds: [],
    });

    return <Dialog open={true} fullWidth maxWidth="lg">
        <DialogTitle>
            {
                props.editingNotification
                    ? t("genericInAppNotificationsPage.editTitle")
                    : t("genericInAppNotificationsPage.newTitle")
            }
        </DialogTitle>

        <Stack direction="row" sx={{ m: 3, mt: 0 }} gap={4}>
            <Box
                gap={1}
                flex={1}
                display="flex"
                width="500px"
                flexDirection="column" alignItems="stretch">

                <TranslatableTextField
                    label={t("genericInAppNotificationsPage.columns.title")}
                    value={formData.title}
                    onChange={(v) => setFormData({
                        ...formData,
                        title: v,
                    })}
                    entityType="GenericInAppNotification"
                    entityId={String(formData.id)}
                    property="title"
                />
                <TranslatableTextField
                    label={t("genericInAppNotificationsPage.columns.message")}
                    value={formData.message}
                    onChange={(v) => setFormData({
                        ...formData,
                        message: v,
                    })}
                    entityType="GenericInAppNotification"
                    entityId={String(formData.id)}
                    property="message"
                />
            </Box>

            <Box flex={1}>
                <Box display="flex" flexDirection="column">
                    <Typography variant="subtitle2">
                        {t("genericInAppNotificationsPage.visibility")}
                    </Typography>

                    <FormControlLabel
                        label={t("serviceModulesPage.companyWideAssigned")}
                        control={
                            <Checkbox
                                checked={formData.isCompanyWideAssigned}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    isCompanyWideAssigned: v.target.checked
                                })}
                            />
                        }
                    />

                    {
                        !formData.isCompanyWideAssigned &&
                        <>
                            <SelectGroupsList
                                sx={{ ml: 4, mt: 1 }}
                                value={formData.assignedGroupIds}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    assignedGroupIds: v
                                })}
                            />
                            <SelectEmployeesList
                                value={formData.assignedEmployeeUserIds}
                                onChange={(v) => setFormData({
                                    ...formData,
                                    assignedEmployeeUserIds: v
                                })} />
                        </>
                    }
                </Box>
            </Box>
        </Stack>

        <DialogActions>
            {props.onDelete && <GeneralButton
                appVariant="danger"
                onClick={async () => {
                    setIsDeleting(true);
                    await props.onDelete!(formData);
                    setIsDeleting(false);
                }}
                disabled={isSaving || isDeleting}
                loading={isDeleting}>
                {t("buttons.delete")}
            </GeneralButton>}

            <GeneralButton
                appVariant="secondary"
                onClick={props.onClose}
                disabled={isSaving || isDeleting}>
                {t("buttons.cancel")}
            </GeneralButton>

            <GeneralButton
                appVariant="primary"
                onClick={async () => {
                    setIsSaving(true);
                    await props.onSave(formData);
                    setIsSaving(false);
                }}
                disabled={isSaving || isDeleting}
                loading={isSaving}
            >
                {t("buttons.save")}
            </GeneralButton>
        </DialogActions>
    </Dialog>
}